import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Radio,
  RadioChangeEvent,
  Row,
} from 'antd';
import React, { useCallback, useState } from 'react';

import { useRatesAdjustBaseRate } from '../../hooks/apiHooks/rate';
import { Carrier } from '../../models/Carrier';
import { Rate } from '../../models/Rate';

const OPTION_DOLLAR = 'dollar';
const OPTION_PERCENT = 'percent';

interface IProps {
  carrier?: Carrier;
  rates: Rate[];
  onSubmit: () => void;
  onClose: () => void;
}

const MultipleBaseRateAdjustModal: React.FC<IProps> = ({
  carrier,
  onClose,
  onSubmit,
  rates,
}) => {
  const { isLoading: isSubmitting, mutate } = useRatesAdjustBaseRate();

  const [form] = Form.useForm();
  const [selectedType, setSelectedType] = useState('dollar');

  const closeModal = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  type FormState = {
    amountDollars: number;
    amountPercent: number;
    note?: string;
    type: string;
  };

  const submitForm = useCallback(
    (formState: FormState) => {
      let finalValue =
        formState.type === OPTION_DOLLAR
          ? formState.amountDollars
          : formState.amountPercent;
      mutate(
        [
          rates.map((rate) => rate.id),
          finalValue,
          formState.type,
          carrier?.id,
          formState.note?.trim() || undefined,
        ],
        {
          onError: (error) => {
            notification.error({
              description: error.message,
              message: 'Failed to adjust rates',
            });
          },
          onSuccess: () => {
            onSubmit();
            closeModal();
          },
        }
      );
    },
    [carrier?.id, closeModal, mutate, onSubmit, rates]
  );

  const onTypeSelect = (e: RadioChangeEvent) => {
    setSelectedType(e.target.value);
    form.setFieldsValue({
      amountDollars: undefined,
      amountPercent: undefined,
      type: e.target.value,
    });
  };

  return (
    <Modal
      centered
      footer={null}
      maskClosable={false}
      open={true}
      title="Adjust Base Rates"
      width={600}
      onCancel={closeModal}
    >
      <Form
        form={form}
        initialValues={{
          amountDollars: undefined,
          amountPercent: undefined,
          note: undefined,
          type: OPTION_DOLLAR,
        }}
        layout="vertical"
        onFinish={submitForm}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item label="Type" name="type">
              <Radio.Group value={selectedType} onChange={onTypeSelect}>
                <Radio.Button value={OPTION_DOLLAR}>$ Amount</Radio.Button>
                <Radio.Button value={OPTION_PERCENT}>% Rate</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}>
            {selectedType === OPTION_DOLLAR && (
              <Form.Item
                label="$ Amount"
                name="amountDollars"
                rules={[{ message: 'Amount is required!', required: true }]}
              >
                <InputNumber
                  formatter={(value) => `$ ${value ?? ''}`}
                  parser={(value) =>
                    String(value ?? '')
                      .replace(/\$\s?/g, '')
                      .trim()
                  }
                />
              </Form.Item>
            )}
            {selectedType === OPTION_PERCENT && (
              <Form.Item
                label="% Rate"
                name="amountPercent"
                rules={[{ message: 'Amount is required!', required: true }]}
              >
                <InputNumber
                  formatter={(value) => `${value ?? ''}%`}
                  parser={(value) =>
                    String(value ?? '')
                      .replace(/%/g, '')
                      .trim()
                  }
                />
              </Form.Item>
            )}
          </Col>
          <Col span={16}>
            <Form.Item label="Note" name="note">
              <Input.TextArea
                autoSize={{ maxRows: 2, minRows: 1 }}
                placeholder="Optional note for this adjustment"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item>
              <Button className="modal-close-btn" onClick={closeModal}>
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <Button
                className="modal-save-btn"
                disabled={isSubmitting}
                htmlType="submit"
                loading={isSubmitting}
                type="primary"
              >
                SAVE
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default MultipleBaseRateAdjustModal;
